import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import * as Sentry from '@sentry/browser';

import CheckoutSessionOnboarding, {
  CheckoutSessionOnboardingStatus,
  CheckoutSessionOnboardingErrorCode,
} from '../../../Model/CheckoutSessionOnboarding';
import UserContract from '../../../Model/UserContract';
import LoadingPage from '../../../components/LoadingPage';
import DownloadApp from '../../../components/DownloadApp';
import {
  CommitmentStickerFlow,
  EntryPoint,
  ShareCardStatus,
  useShareCard,
} from '../../../components/StoryShare';
import LightAppPreview from '../../../assets/light-app-preview.png';

import {
  Container as CheckoutThemeContainer,
  ContentContainer,
  LeftSideWrapper,
  RightSideWrapper,
  StyledAppPreview,
  Title,
  DownloadText,
  ChecklistContainer,
  ChecklistTitle,
  ChecklistItem,
  NextSteps,
  StyledCheckIcon,
} from '../../Home/styles';

import {
  Container,
  ErrorContainer,
  ErrorMessage,
  LoadingMessage,
  EmailSection,
  EmailLabel,
  EmailText,
  WrongEmailSection,
  WrongEmailTitle,
  Description,
  ChangeEmailButton,
  SupportText,
  FormSuccessContainer,
  FormSuccessMessage,
  FormNewEmailMessage,
  UpsellContentContainer,
  UpsellPreviewWrapper,
  UpsellAppPreview,
  AccountPanel,
} from './styles';
import ChangeEmailForm from './ChangeEmailForm';
import texts from './texts.json';

const QUERY_PARAM_CHECKOUT_SESSION_ID = 'checkoutSessionOnboardingId';

/*
  The challenge whose commitment sticker this page offers before the app download, as Home does after the
  embedded checkout: only when the contract bought has sharing on. An offer bought inside the app has no app
  left to get. A failed read only costs the sticker.
*/
const getStoryChallengeId = async ({ contractId, isUpsell }) => {
  if (isUpsell || !contractId) {
    return null;
  }
  try {
    const contract = await UserContract.getContractById(contractId);
    return (contract.isStoryShareEnabled && contract.challengeId) || null;
  } catch (error) {
    Sentry.captureException(error, { extra: { contractId } });
    return null;
  }
};

const StripeCheckoutSuccess = () => {
  const location = useLocation();
  const [checkoutDoc, setCheckoutDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [showChangeEmailForm, setShowChangeEmailForm] = useState(false);
  const [displayEmail, setDisplayEmail] = useState('');
  const [emailChangedSuccess, setEmailChangedSuccess] = useState(false);
  const [storyChallengeId, setStoryChallengeId] = useState(null);
  const { status: shareCardStatus, card: shareCard } = useShareCard(storyChallengeId);

  // Extract session ID from URL query params
  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get(QUERY_PARAM_CHECKOUT_SESSION_ID);
  }, [location.search]);

  useEffect(() => {
    const fetchCheckoutSession = async () => {
      if (!sessionId) {
        setFetchError(true);
        setIsLoading(false);
        return;
      }

      try {
        const doc = await CheckoutSessionOnboarding.getById(sessionId);
        if (doc) {
          setCheckoutDoc(doc);
          setDisplayEmail(doc.email);
          setStoryChallengeId(await getStoryChallengeId(doc));
        } else {
          setFetchError(true);
        }
      } catch (error) {
        setFetchError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCheckoutSession();
  }, [sessionId]);

  const handleChangeEmailSuccess = useCallback((newEmail) => {
    setDisplayEmail(newEmail);
    setShowChangeEmailForm(false);
    setEmailChangedSuccess(true);
  }, []);

  const renderEmailSection = () => {
    if (emailChangedSuccess) {
      const newEmailText = texts.newEmail.replace('{email}', displayEmail);
      return (
        <FormSuccessContainer>
          <FormSuccessMessage>{texts.emailChangedSuccess}</FormSuccessMessage>
          <FormNewEmailMessage>{ReactHtmlParser(newEmailText)}</FormNewEmailMessage>
        </FormSuccessContainer>
      );
    }

    if (showChangeEmailForm) {
      return (
        <ChangeEmailForm
          sessionId={sessionId}
          currentEmail={displayEmail}
          onCancel={() => setShowChangeEmailForm(false)}
          onSuccess={handleChangeEmailSuccess}
        />
      );
    }

    return (
      <WrongEmailSection>
        <WrongEmailTitle>{texts.wrongEmailTitle}</WrongEmailTitle>
        <Description>{texts.updateEmailDescription}</Description>
        <ChangeEmailButton onClick={() => setShowChangeEmailForm(true)}>
          {texts.changeEmail}
        </ChangeEmailButton>
      </WrongEmailSection>
    );
  };

  // Show initial loading spinner while fetching the document, and the sticker's challenge when there is one
  if (isLoading || shareCardStatus === ShareCardStatus.LOADING) {
    return (
      <Container>
        <LoadingPage />
      </Container>
    );
  }

  const { status, isUpsell } = checkoutDoc || {};

  // Show error if no sessionId or document not found, handle ERROR status
  if (fetchError || !checkoutDoc || status === CheckoutSessionOnboardingStatus.ERROR) {
    const errorCode = checkoutDoc?.errorCode;
    const isDuplicateCoachAccount = errorCode === CheckoutSessionOnboardingErrorCode.DUPLICATE_COACH_ACCOUNT;
    const errorTitle = isDuplicateCoachAccount ? texts.duplicateAccountTitle : texts.errorTitle;
    const duplicateMessage = checkoutDoc?.refunded === false
      ? texts.duplicateAccountMessagePending
      : texts.duplicateAccountMessage;
    const errorMessage = isDuplicateCoachAccount ? duplicateMessage : texts.errorMessage;
    const errorEmail = checkoutDoc?.email || displayEmail;

    return (
      <Container>
        <ContentContainer style={{ alignItems: 'center' }}>
          <RightSideWrapper>
            <ErrorContainer>
              <DownloadText>{errorTitle}</DownloadText>
              <ErrorMessage>{errorMessage}</ErrorMessage>
              {isDuplicateCoachAccount && errorEmail && (
                <EmailSection>
                  <EmailLabel>{texts.duplicateAccountEmailLabel}</EmailLabel>
                  <EmailText>{errorEmail}</EmailText>
                </EmailSection>
              )}
            </ErrorContainer>
          </RightSideWrapper>
        </ContentContainer>
      </Container>
    );
  }

  /*
    First purchase: the buyer has no account yet, so walk them through installing the app
    and signing in with the email the account was created under.
  */
  const renderOnboardingSteps = () => (
    <>
      <DownloadText>{texts.downloadApp}</DownloadText>
      <ChecklistContainer>
        <ChecklistTitle style={{ fontSize: '14px' }}>{texts.whatNeedToDoNext}</ChecklistTitle>
        <ChecklistItem>
          <StyledCheckIcon />
          <NextSteps>{ReactHtmlParser(texts.downloadAppUsingLink)}</NextSteps>
        </ChecklistItem>
        <ChecklistItem>
          <StyledCheckIcon />
          <NextSteps>{ReactHtmlParser(texts.clickSendSignInLink)}</NextSteps>
        </ChecklistItem>
        <ChecklistItem>
          <StyledCheckIcon />
          <NextSteps>{ReactHtmlParser(texts.completeOnboarding)}</NextSteps>
        </ChecklistItem>
      </ChecklistContainer>
      {renderEmailSection()}
      <DownloadApp />
    </>
  );

  /*
    Upsell bought from inside the app: the buyer is already a signed-in client, so the
    install steps and the change-email form would only send them down a path they have
    already completed. One line saying there is nothing left to do.
  */
  const renderUpsellConfirmation = () => (
    <>
      <DownloadText>{texts.upsellAllSetTitle}</DownloadText>
      <Description>{texts.upsellAllSet}</Description>
    </>
  );

  const linkedEmail = (
    <EmailSection>
      <EmailLabel>{texts.accountAssociatedWith}</EmailLabel>
      <EmailText>{displayEmail}</EmailText>
    </EmailSection>
  );
  const supportLine = <SupportText>{ReactHtmlParser(texts.contactSupport)}</SupportText>;

  // The sticker first, ending on its own app download, with the account's email under it.
  if (status === CheckoutSessionOnboardingStatus.COMPLETE && shareCardStatus === ShareCardStatus.READY) {
    return (
      <CheckoutThemeContainer $isDarkTheme={shareCard.isDarkTheme}>
        <CommitmentStickerFlow
          card={shareCard}
          entryPoint={EntryPoint.CHECKOUT}
          // Stripe Hosted Checkout offers no 1:1 (useCheckoutUpsell).
          addedCoaching={false}
          appFooter={(
            <AccountPanel>
              {linkedEmail}
              {renderEmailSection()}
              {supportLine}
            </AccountPanel>
          )}
        />
      </CheckoutThemeContainer>
    );
  }

  // Handle COMPLETE status - show download links
  if (status === CheckoutSessionOnboardingStatus.COMPLETE) {
    // The upsell variant keeps the app preview on phones; see UpsellContentContainer.
    const Content = isUpsell ? UpsellContentContainer : ContentContainer;
    const PreviewWrapper = isUpsell ? UpsellPreviewWrapper : LeftSideWrapper;
    const AppPreview = isUpsell ? UpsellAppPreview : StyledAppPreview;
    return (
      <Container>
        <Content style={{ alignItems: 'center' }}>
          <PreviewWrapper>
            <AppPreview
              src={LightAppPreview}
              alt="App Preview"
            />
          </PreviewWrapper>
          <RightSideWrapper>
            {linkedEmail}
            <Title>
              {texts.thankYouForPayment}
            </Title>
            {isUpsell ? renderUpsellConfirmation() : renderOnboardingSteps()}
            {supportLine}
          </RightSideWrapper>
        </Content>
      </Container>
    );
  }

  // Default to loading for any other status (e.g., CREATED, IN_PROGRESS)
  return (
    <Container>
      <LoadingPage
        messageComponent={(
          <LoadingMessage>{isUpsell ? texts.loadingMessageUpsell : texts.loadingMessage}</LoadingMessage>
        )}
      />
    </Container>
  );
};

export default observer(StripeCheckoutSuccess);
